import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import './scss/Navigation.scss';
import { logout } from './utils/auth';
import { authService } from './services/authService';
import { isLeaderboardHidden } from './utils/teamSettings';
import { getCachedAcademies, setCachedAcademies, ACADEMY_ADMIN_API } from './utils/academyAccess';
import api from './services/apiService';
import {
  FaClipboard,
  FaClipboardList,
  FaCog,
  FaCreditCard,
  FaFutbol,
  FaHome,
  FaMapMarkerAlt,
  FaPowerOff,
  FaQuestionCircle,
  FaRunning,
  FaShieldAlt,
  FaTrophy,
  FaUser,
  FaUserCircle,
  FaUserFriends,
  FaUsers,
} from 'react-icons/fa';

function NavItem({ to, icon: Icon, children, onClick }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) => (isActive ? 'active' : '')}
        onClick={onClick}
      >
        {Icon && <Icon className="nav-icon" aria-hidden="true" />}
        <span>{children}</span>
      </NavLink>
    </li>
  );
}

function NavDivider() {
  return <li className="nav-divider" aria-hidden="true" />;
}

function Navigation({ onNavigate }) {
  const isManager = sessionStorage.getItem('is_manager') === 'true';
  const userType = sessionStorage.getItem('user_type');
  const teamId = sessionStorage.getItem('selected_team_id');
  const [childName, setChildName] = useState('');
  const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
  const isSuperAdmin = authService.isSuperAdmin();
  const [showSubscription, setShowSubscription] = useState(false);
  const [hasAcademyAccess, setHasAcademyAccess] = useState(getCachedAcademies().length > 0);
  const showAdmin = authService.isAdmin() || authService.isManager() || userType === 'manager' || userType === 'coach' || isSuperAdmin || hasAcademyAccess;

  useEffect(() => {
    if (!teamId) {
      setShowSubscription(false);
      return;
    }
    let cancelled = false;
    setShowSubscription(false);
    const loadAcademy = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_academy_subscription.php?team_id=${teamId}`);
        const data = response.data;
        if (cancelled) {
          return;
        }
        if (!data.success) {
          setShowSubscription(false);
          return;
        }
        const stripeConnected = !!(data.stripe_connected || data.academy?.stripe_connected);
        setShowSubscription(!!(data.assigned && stripeConnected));
      } catch (error) {
        console.error('Could not load academy subscription', error);
        if (!cancelled) {
          setShowSubscription(false);
        }
      }
    };
    loadAcademy();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  useEffect(() => {
    if (isSuperAdmin) {
      return;
    }
    let cancelled = false;
    const loadMyAcademies = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`);
        if (cancelled || !response.data?.success) {
          return;
        }
        const list = response.data.academies || [];
        setCachedAcademies(list);
        setHasAcademyAccess(list.length > 0);
      } catch (error) {
        if (!cancelled) {
          setHasAcademyAccess(getCachedAcademies().length > 0);
        }
      }
    };
    loadMyAcademies();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isParent) {
      return;
    }

    const storedChildName = sessionStorage.getItem('child_name');
    if (storedChildName) {
      setChildName(storedChildName);
      return;
    }

    const fetchChildName = async () => {
      const playerId = sessionStorage.getItem('player_id');
      if (!playerId) {
        return;
      }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/get_player.php?player_id=${playerId}`);
        const data = await response.json();
        if (data.success && data.player) {
          const fullName = `${data.player.first_name} ${data.player.last_name}`;
          setChildName(fullName);
          sessionStorage.setItem('child_name', fullName);
        }
      } catch (error) {
        console.error('Error fetching child name:', error);
      }
    };

    fetchChildName();
  }, [isParent]);

  const handleNavClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <nav className="navigation">
      <ul>
        {!isSuperAdmin && (
          <NavItem to="/dashboard" icon={FaHome} onClick={handleNavClick}>Home</NavItem>
        )}
        <NavItem to="/matches" icon={FaFutbol} onClick={handleNavClick}>Fixtures</NavItem>

        {(userType === 'manager' || userType === 'coach' || userType === 'parent') && (
          <NavItem to="/trainingSchedule" icon={FaRunning} onClick={handleNavClick}>Training</NavItem>
        )}

        {(userType === 'manager' || userType === 'coach') && (
          <NavItem to="/players" icon={FaUsers} onClick={handleNavClick}>Players</NavItem>
        )}

        {(authService.isAdmin() || userType === 'coach') && (
          <NavItem to="/player-attendance" icon={FaClipboardList} onClick={handleNavClick}>Player Attendance</NavItem>
        )}

        {!isManager && (
          <NavItem to="/profile" icon={FaUserCircle} onClick={handleNavClick}>
            {isParent && childName ? `${childName.split(' ')[0]}'s Player Profile` : 'My Profile'}
          </NavItem>
        )}

        <NavItem to="/match-reports" icon={FaClipboard} onClick={handleNavClick}>Match Reports</NavItem>

        {!isLeaderboardHidden() && (
          <NavItem to="/leaderboard" icon={FaTrophy} onClick={handleNavClick}>Leaderboard</NavItem>
        )}

        <NavDivider />

        {userType === 'parent' && (
          <NavItem to="/parent/resources" icon={FaUsers} onClick={handleNavClick}>Parent Resources</NavItem>
        )}

        {userType === 'parent' && authService.canRespondToMatches(sessionStorage.getItem('player_id')) && (
          <NavItem to="/parent/settings" icon={FaUserFriends} onClick={handleNavClick}>Child Access Settings</NavItem>
        )}

        <NavItem to={`/team/${teamId}`} icon={FaShieldAlt} onClick={handleNavClick}>Team Information</NavItem>

        <NavDivider />

        <NavItem to="/my-account" icon={FaUser} onClick={handleNavClick}>My Account</NavItem>
        {showSubscription && (
          <NavItem to="/membership" icon={FaCreditCard} onClick={handleNavClick}>Team Monthly Subscription</NavItem>
        )}
        <NavItem to="/help" icon={FaQuestionCircle} onClick={handleNavClick}>Help</NavItem>

        <NavDivider />

        {showAdmin && (
          <NavItem to="/admin" icon={FaCog} onClick={handleNavClick}>Admin</NavItem>
        )}
        {isSuperAdmin && (
          <NavItem to="/pitches" icon={FaMapMarkerAlt} onClick={handleNavClick}>Locations</NavItem>
        )}
        <li>
          <a
            onClick={() => {
              handleNavClick();
              logout();
            }}
            className="logout-button"
          >
            <FaPowerOff className="nav-icon" aria-hidden="true" />
            <span>Log Out</span>
          </a>
        </li>
      </ul>
    </nav>
  );
}

export default Navigation;
